// Wrapper para subscriptions GraphQL (AppSync) com reconnect automatico.
//
// Antes, o padrao no codigo era:
//   const sub = API.graphql(...).subscribe({ next, error: () => sub.unsubscribe() });
// — se a subscription caisse (queda de wifi, servidor reiniciando, token
// expirando), ela simplesmente parava. Tela ficava com dados congelados
// sem aviso.
//
// Esta funcao envolve esse padrao:
// - Em caso de erro, faz unsubscribe da subscription atual e tenta de
//   novo apos um delay com exponential backoff (1s, 2s, 4s, ..., 30s max)
// - Reseta o backoff quando recebe a primeira mensagem com sucesso
// - Para de tentar quando o consumidor chama .unsubscribe() no objeto
//   retornado
//
// Uso:
//   const recon = createReconnectingSubscription({
//     buildSubscription: ({ next, error }) =>
//       API.graphql(graphqlOperation(onUpdateIOT, { thingName }))
//         .subscribe({ next, error }),
//     onNext: (data) => { ... },
//     onError: (err) => { ... }, // opcional
//     label: 'onUpdateIOT/thingName', // opcional, ajuda em logs
//   });
//   ...
//   recon.unsubscribe(); // parar totalmente

const DEFAULT_INITIAL_DELAY = 1000;
const DEFAULT_MAX_DELAY = 30000;

export function createReconnectingSubscription({
  buildSubscription,
  onNext,
  onError,
  initialDelay = DEFAULT_INITIAL_DELAY,
  maxDelay = DEFAULT_MAX_DELAY,
  label = 'subscription',
}) {
  let stopped = false;
  let currentSub = null;
  let retryTimer = null;
  let attempt = 0;

  const safeUnsub = () => {
    if (currentSub) {
      try {
        currentSub.unsubscribe();
      } catch (e) {
        // ignore
      }
      currentSub = null;
    }
  };

  const connect = () => {
    if (stopped) return;
    try {
      currentSub = buildSubscription({
        next: (data) => {
          if (stopped) return;
          // Sucesso — zera o backoff para a proxima eventual queda
          attempt = 0;
          if (typeof onNext === 'function') onNext(data);
        },
        error: (e) => {
          if (stopped) return;
          // eslint-disable-next-line no-console
          console.warn(
            `[reconnecting-subscription:${label}] erro, tentando reconectar`,
            e
          );
          safeUnsub();
          if (typeof onError === 'function') {
            try {
              onError(e);
            } catch (cbErr) {
              // ignore
            }
          }
          const delay = Math.min(
            initialDelay * 2 ** attempt,
            maxDelay
          );
          attempt += 1;
          retryTimer = setTimeout(connect, delay);
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[reconnecting-subscription:${label}] falha ao criar`,
        e
      );
      // Mesmo padrao de retry no caso da chamada inicial falhar
      const delay = Math.min(initialDelay * 2 ** attempt, maxDelay);
      attempt += 1;
      retryTimer = setTimeout(connect, delay);
    }
  };

  connect();

  return {
    unsubscribe: () => {
      stopped = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      safeUnsub();
    },
  };
}
